import { ActionCreator } from 'redux'
import { ThunkAction, ThunkDispatch } from 'redux-thunk'
import { ProjectsApi } from '../../../api/projectApi'
import { toastMsg } from '../../../components/Toast/toastMsg'
import { ProjectsState } from '../reducers'
import {
  ICurrentProjectListEntry,
  IProjectFilters,
  IProjectsHealthWidget,
  IProjectTypeFilter,
  IProjectsResponse,
  ITimeTrackingProjectsResponse
} from '../types/project'
import * as constants from './constants'

export interface GetProjectsSuccess {
  type: constants.GET_PROJECTS_SUCCESS
  projects: IProjectsResponse
}

export interface GetCurrentProjectsListSuccess {
  type: constants.GET_CURRENT_PROJECTS_SUCCESS
  projects: ICurrentProjectListEntry[]
}

export interface FilterProjects {
  type: constants.FILTER_PROJECTS
  filters: IProjectFilters
}

export interface GetProjectsHealthSuccess {
  type: constants.GET_PROJECTS_HEALTH_WIDGET_SUCCESS
  widgetData: IProjectsHealthWidget
}

export interface GetProjectsHealthErrored {
  type: constants.GET_PROJECTS_HEALTH_WIDGET_HAS_ERROR
  hasErrored: boolean
}

export interface GetProjectsErrored {
  type: constants.GET_PROJECTS_LIST_HAS_ERROR
  hasErrored: boolean
}

export interface SetProjectTypeFilter {
  type: constants.SET_PROJECT_TYPE_FILTER
  filter: IProjectTypeFilter
}

export interface UpdateProjectTypeFilterCounts {
  type: constants.UPDATE_PROJECT_TYPE_FILTER_COUNTS
  counts: {
    all: number
    sponsor: number
    service_provider: number
    internal: number
    archive: number
  }
}

export interface SortProjects {
  type: constants.SORT_PROJECTS
  sort: { name: string; direction: string }
}

export interface GetProjectsTTListSuccess {
  type: constants.GET_PROJECTS_TT_LIST_SUCCESS
  projects: ITimeTrackingProjectsResponse
}

export type ProjectsThunkResult<R> = ThunkAction<Promise<R>, ProjectsState, null, ProjectsActions>
export type ProjectsThunkDispatchType = ThunkDispatch<ProjectsState, null, ProjectsActions>

export const getProjects: ActionCreator<ProjectsThunkResult<IProjectsResponse>> = (
  queryParams: Record<string, string>
): ProjectsThunkResult<IProjectsResponse> => {
  return async (dispatch: ProjectsThunkDispatchType) => {
    return ProjectsApi.getProjects(queryParams)
      .then((response) => {
        dispatch(getProjectsSuccess(response))
        return response
      })
      .catch((error) => {
        dispatch(getProjectsHasErrored(true, error.response.data, constants.GET_PROJECTS))
        return {} as IProjectsResponse
      })
  }
}

export const getProjectsSuccess = (projects: IProjectsResponse): GetProjectsSuccess => {
  return {
    type: constants.GET_PROJECTS_SUCCESS,
    projects,
  }
}

export const getCurrentProjectsSuccess = (projects: ICurrentProjectListEntry[]): GetCurrentProjectsListSuccess => {
  return {
    type: constants.GET_CURRENT_PROJECTS_SUCCESS,
    projects,
  }
}

export const getProjectsHasErrored = (hasErrored: boolean, error: Error, action?: string): GetProjectsErrored => {
  if (action) {
    const errorMsg = error.message
    toastMsg(errorMsg, action, 'error')
  }
  return {
    type: constants.GET_PROJECTS_LIST_HAS_ERROR,
    hasErrored: hasErrored,
  }
}

export const filterProjects = (filters: IProjectFilters): FilterProjects => {
  return {
    type: constants.FILTER_PROJECTS,
    filters,
  }
}

export const getProjectsHealthWidget: ActionCreator<ProjectsThunkResult<IProjectsHealthWidget>> = (): ProjectsThunkResult<IProjectsHealthWidget> => {
  return async (dispatch: ProjectsThunkDispatchType) => {
    return ProjectsApi.getProjectsHealthWidget()
      .then((response) => {
        dispatch(getProjectsHealthWidgetSuccess(response))
        return response
      })
      .catch((error) => {
        dispatch(getProjectsHasErrored(true, error.response.data, constants.GET_PROJECTS_HEALTH_WIDGET_HAS_ERROR))
        return {} as IProjectsHealthWidget
      })
  }
}

export const getProjectsHealthWidgetSuccess = (widgetData: IProjectsHealthWidget): GetProjectsHealthSuccess => {
  return {
    type: constants.GET_PROJECTS_HEALTH_WIDGET_SUCCESS,
    widgetData
  }
}

export const getProjectsHealthWidgetHasErrored = (hasErrored: boolean, error: Error, action?: string): GetProjectsHealthErrored => {
  if (action) {
    const errorMsg = error.message
    toastMsg(errorMsg, action, 'error')
  }
  return {
    type: constants.GET_PROJECTS_HEALTH_WIDGET_HAS_ERROR,
    hasErrored: hasErrored,
  }
}

export const setProjectTypeFilter = (filter: IProjectTypeFilter): SetProjectTypeFilter => {
  return {
    type: constants.SET_PROJECT_TYPE_FILTER,
    filter,
  }
}

export const updateProjectTypeFilterCounts = (counts: {
  all: number
  sponsor: number
  service_provider: number
  internal: number
  archive: number
}): UpdateProjectTypeFilterCounts => {
  return {
    type: constants.UPDATE_PROJECT_TYPE_FILTER_COUNTS,
    counts,
  }
}

export const sortProjects = (sort: { name: string; direction: string }): SortProjects => {
  return {
    type: constants.SORT_PROJECTS,
    sort,
  }
}

export const getProjectsTTList: ActionCreator<ProjectsThunkResult<ITimeTrackingProjectsResponse>> = (): ProjectsThunkResult<ITimeTrackingProjectsResponse> => {
  return async (dispatch: ProjectsThunkDispatchType) => {
    return ProjectsApi.getTimeTrackingProjects()
      .then((response) => {
        dispatch(getProjectsTTListSuccess(response))
        return response
      })
      .catch((error) => {
        dispatch(getProjectsHasErrored(true, error.response.data, constants.GET_PROJECTS_TT_LIST))
        return {} as ITimeTrackingProjectsResponse
      })
  }
}
export const getCurrentProjectsList: ActionCreator<ProjectsThunkResult<ICurrentProjectListEntry[]>> = (
  queryParams: Record<string, string>
): ProjectsThunkResult<ICurrentProjectListEntry[]> => {
  return async (dispatch: ProjectsThunkDispatchType) => {
    return ProjectsApi.getCurrentProjectsList(queryParams)
      .then((response) => {
        dispatch(getCurrentProjectsSuccess(response))
        return response
      })
      .catch((error) => {
        dispatch(getProjectsHasErrored(true, error, constants.GET_PROJECTS_TT_LIST))
        return {} as ICurrentProjectListEntry[]
      })
  }
}

export const getProjectsTTListSuccess = (projects: ITimeTrackingProjectsResponse): GetProjectsTTListSuccess => {
  return {
    type: constants.GET_PROJECTS_TT_LIST_SUCCESS,
    projects,
  }
}

export type ProjectsActions =
  | GetProjectsSuccess
  | GetCurrentProjectsListSuccess
  | FilterProjects
  | GetProjectsErrored
  | SetProjectTypeFilter
  | UpdateProjectTypeFilterCounts
  | SortProjects
  | GetProjectsTTListSuccess
  | GetProjectsHealthSuccess
  | GetProjectsHealthErrored
